import { useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { contentInfo, slugify, type ContentListItem, type ContentTypeValue, type LocaleView } from '@agency/contracts';
import { Badge, Button, Drawer, EmptyState, Notice, SelectField, Skeleton, TextField, scrollRegion, useToast } from '@agency/ui';
import { ApiClientError, get, post } from '../../lib/api.ts';
import { fmtDate } from '../../lib/format.ts';
import { QueryError } from '../../lib/query-state.tsx';
import { useAuth } from '../../lib/auth.tsx';
import '../landings/landings.css';

function NewContentDrawer({ type, open, onClose }: { type: ContentTypeValue; open: boolean; onClose: () => void }) {
  const nav = useNavigate();
  const qc = useQueryClient();
  const toast = useToast();
  const locales = useQuery<LocaleView[]>({ queryKey: ['locale-options'], queryFn: () => get('/admin/site/locale-options'), enabled: open, staleTime: 30_000 });
  const [title, setTitle] = useState('');
  const [locale, setLocale] = useState('');
  const [slug, setSlug] = useState<string | null>(null);
  const loc = locale || locales.data?.find((l) => l.isDefault)?.code || 'en';
  const info = contentInfo[type];
  const derived = useMemo(() => (title.trim() ? slugify(title, loc).slice(0, 100) : ''), [title, loc]);
  const m = useMutation<{ id: string; slug: string }, ApiClientError, void>({
    mutationFn: () => post('/admin/content', { type, title: title.trim(), locale: loc, slug: slug ?? derived }),
    onSuccess: (r) => { void qc.invalidateQueries({ queryKey: ['content'] }); toast({ tone: 'success', message: `Draft created. Replace the placeholder copy before publishing.` }); setTitle(''); setSlug(null); onClose(); nav(`/content/${r.id}`); },
  });
  const fe = (f: string) => m.error?.fieldErrors.find((x) => x.field === f)?.message;
  return (
    <Drawer open={open} onClose={() => { m.reset(); onClose(); }} title={`New ${info.label.toLowerCase()}`}
      footer={<><Button onClick={onClose}>Cancel</Button><Button variant="primary" loading={m.isPending} disabled={title.trim().length < 2} onClick={() => m.mutate()}>Create draft</Button></>}>
      <div style={{ display: 'grid', gap: 12 }}>
        <TextField label="Title" required value={title} onChange={(e) => setTitle(e.target.value)} data-autofocus error={fe('title')} />
        <SelectField label="Language" value={loc} onChange={(e) => setLocale(e.target.value)}>
          {(locales.data ?? []).map((l) => <option key={l.code} value={l.code}>{l.name}</option>)}
        </SelectField>
        <TextField label="URL path" className="ag-mono" value={slug ?? derived} onChange={(e) => setSlug(e.target.value)} hint={`Published at /${info.prefix}${slug ?? derived}`} error={fe('slug')} />
        {m.isError && !m.error.fieldErrors.length ? <Notice tone="error" title="Could not create">{m.error.message}</Notice> : null}
      </div>
    </Drawer>
  );
}

export function ContentListPage({ type }: { type: ContentTypeValue }) {
  const { can } = useAuth();
  const info = contentInfo[type];
  const [newOpen, setNewOpen] = useState(false);
  const [params, setParams] = useSearchParams();
  const showArchived = params.get('archived') === '1';
  const q = useQuery<ContentListItem[], ApiClientError>({ queryKey: ['content', type], queryFn: () => get(`/admin/content?type=${type}`) });
  const rows = (q.data ?? []).filter((r) => r.archived === showArchived);
  return (
    <>
      <div className="pagehead">
        <div>
          <h1>{info.plural}</h1>
          <p>{type === 'CASE_STUDY' ? 'Proof of delivered work. Named clients need permission on record, and only verified results appear publicly.' : 'Insights for readers and search. Publishing needs an author and a summary; published versions are never edited in place.'}</p>
        </div>
        {can('content.edit') ? <Button variant="primary" icon="plus" onClick={() => setNewOpen(true)}>New {info.label.toLowerCase()}</Button> : null}
      </div>
      <div className="filters">
        <label className="choice-inline"><input type="checkbox" checked={showArchived} onChange={(e) => setParams(e.target.checked ? { archived: '1' } : {}, { replace: true })} /> Show archived</label>
      </div>
      {q.isLoading ? <div role="status"><span className="ag-visually-hidden">Loading…</span><Skeleton height={140} /></div>
        : q.isError ? <QueryError error={q.error} onRetry={() => void q.refetch()} />
        : rows.length === 0 ? <EmptyState title={showArchived ? 'Nothing archived' : `No ${info.plural.toLowerCase()} yet`} icon="file" action={!showArchived && can('content.edit') ? <Button variant="primary" icon="plus" onClick={() => setNewOpen(true)}>New {info.label.toLowerCase()}</Button> : undefined}>Drafts start with clearly marked placeholder copy; nothing is invented for you.</EmptyState>
        : (
          <div className="ag-table-wrap" {...scrollRegion(info.plural)}>
            <table className="ag-table">
              <caption className="ag-visually-hidden">{info.plural}</caption>
              <thead><tr><th scope="col">Title</th><th scope="col">Language</th><th scope="col">Status</th><th scope="col">Live</th><th scope="col">Last edited</th><th scope="col"><span className="ag-visually-hidden">Actions</span></th></tr></thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id} className="ag-row">
                    <td><span className="ag-cell-2"><span>{r.title}</span><span className="ag-mono">/{info.prefix}{r.slug}</span></span></td>
                    <td>{r.locale}</td>
                    <td>{r.archived ? <Badge tone="neutral">Archived</Badge> : r.publishedVersionNumber === null ? <Badge tone="neutral">Draft only</Badge> : r.hasUnpublishedChanges ? <Badge tone="warning">Unpublished changes</Badge> : <Badge tone="success">Published</Badge>}</td>
                    <td className="ag-num">{r.publishedVersionNumber === null ? '—' : `v${r.publishedVersionNumber}`}</td>
                    <td>{fmtDate(r.updatedAt)}</td>
                    <td style={{ textAlign: 'right' }}><Link to={`/content/${r.id}`} className="ag-btn ag-btn--sm" style={{ textDecoration: 'none' }} aria-label={`${can('content.edit') ? 'Edit' : 'View'} ${r.title}`}>{can('content.edit') ? 'Edit' : 'View'}</Link></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      <NewContentDrawer type={type} open={newOpen} onClose={() => setNewOpen(false)} />
    </>
  );
}
